import { FormikProvider, useFormik } from 'formik';
import { isEqual } from 'lodash';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  transferLockPeriodOptions,
  validationSchema,
} from '@/modules/issuer-dashboard/components/modals/IssuerAddClassModal.tsx';
import { ISSUER_REGISTER_NAV_QUERY_KEY } from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  ISSUER_REGISTER_CLASS_LIST_QUERY_KEY,
  useIssuerUpdateRegisterClassMutation,
} from '@/modules/issuer-dashboard/queries/registers.tsx';
import { Button, ModalContentWrapper, Select, TextInputField } from 'ui';
import { AlertIcon } from 'ui/icons';
import { UpdateRegisterClassRequest } from 'api';

export interface IssuerUpdateClassModalProps extends UpdateRegisterClassRequest {
  open: boolean;
  onClose: () => void;
}

const IssuerEditClassModal = ({
  id,
  open,
  class_name,
  min_investment,
  transfer_lock_period,
  current_price_per_share,
  onClose,
}: IssuerUpdateClassModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const formik = useFormik<UpdateRegisterClassRequest>({
    initialValues: {
      id,
      class_name,
      min_investment: min_investment || 0.02,
      transfer_lock_period,
      current_price_per_share,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, { resetForm }) => {
      await updateRegisterClass(values);
      await queryClient.resetQueries({ queryKey: [ISSUER_REGISTER_CLASS_LIST_QUERY_KEY] });
      await queryClient.resetQueries({ queryKey: [ISSUER_REGISTER_NAV_QUERY_KEY] });

      resetForm();
      onClose();
    },
    enableReinitialize: true,
  });

  const { mutateAsync: updateRegisterClass, isLoading } = useIssuerUpdateRegisterClassMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeTransferLockPeriod = (date?: number | null) => {
    formik.setFieldValue('transfer_lock_period', date || null);
  };

  const isChanged = !isEqual(formik.initialValues, formik.values);
  return (
    <ModalContentWrapper open={open} onClose={onClose} title="Edit class">
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col space-y-8">
            <TextInputField name="class_name" label="class name" />
            <TextInputField
              name="current_price_per_share"
              type="number"
              step="0.01"
              label="price per share"
            />
            <TextInputField
              name="min_investment"
              type="number"
              min="0"
              step="0.01"
              label="min. investment (optional)"
            />
            <Select<number | null>
              options={transferLockPeriodOptions}
              label="transfer lock period"
              value={formik.values.transfer_lock_period}
              onChange={handleChangeTransferLockPeriod}
            />
          </div>
          <Button
            loading={isLoading}
            disabled={!isChanged || isLoading || formik.isSubmitting}
            onClick={formik.submitForm}
          >
            Update class
          </Button>
        </form>
        <div
          style={{ backgroundColor: 'rgba(3, 105, 161, 0.08)' }}
          className="flex gap-3 py-3 px-4 rounded mt-5"
        >
          <img src={AlertIcon} alt="info" />
          If you update the price per share, a new {getRegisterTypeStrings('nav')} will be recorded
          with the current date. Alternatively, you can update{' '}
          {getRegisterTypeStrings('nav', { plural: true })} from the {getRegisterTypeStrings('nav')}{' '}
          history.
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default IssuerEditClassModal;
